import * as React from 'react'

export interface SelectDropdownPosition {
  left: number
  /** Fixed width (form/filter selects match the trigger). */
  width?: number
  /** Grow-to-content bounds (inline picker): at least the trigger, never past the viewport. */
  minWidth?: number
  maxWidth?: number
  top?: number
  bottom?: number
  maxHeight: number
}

const GAP = 4
const VIEWPORT_MARGIN = 8
/** Narrowest popover for content-width (inline) triggers, per the approved mockup. */
const INLINE_MIN_WIDTH = 240

const samePosition = (a: SelectDropdownPosition | null, b: SelectDropdownPosition) =>
  a !== null &&
  a.left === b.left &&
  a.width === b.width &&
  a.minWidth === b.minWidth &&
  a.maxWidth === b.maxWidth &&
  a.top === b.top &&
  a.bottom === b.bottom &&
  a.maxHeight === b.maxHeight

/**
 * Fixed-position coordinates for the portalled dropdown so it escapes any
 * overflow:auto ancestor (e.g. a scrollable modal body) instead of clipping.
 * Opens upward when there is too little room below; kept in sync while the
 * page scrolls or resizes. Scrolling inside the popover itself is ignored, and
 * an unchanged position does not re-render the list.
 */
export function useSelectDropdownPosition(
  triggerRef: React.RefObject<HTMLElement | null>,
  popoverRef: React.RefObject<HTMLElement | null>,
  { isOpen, growToContent, maxListHeight }: { isOpen: boolean; growToContent: boolean; maxListHeight: number }
): SelectDropdownPosition | null {
  const [position, setPosition] = React.useState<SelectDropdownPosition | null>(null)

  const update = React.useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
    const spaceAbove = rect.top - VIEWPORT_MARGIN
    const openUp = spaceBelow < 240 && spaceAbove > spaceBelow
    const available = (openUp ? spaceAbove : spaceBelow) - GAP
    const vertical = {
      maxHeight: Math.max(160, Math.min(maxListHeight, available)),
      ...(openUp ? { bottom: window.innerHeight - rect.top + GAP } : { top: rect.bottom + GAP }),
    }

    let next: SelectDropdownPosition
    if (growToContent) {
      // Content-width pill: at least 240px (or the pill), shifted left near the right edge so
      // the list keeps that width instead of shrinking to the pill; never wider than the viewport.
      const viewportWidth = window.innerWidth - VIEWPORT_MARGIN * 2
      const minWidth = Math.min(Math.max(rect.width, INLINE_MIN_WIDTH), viewportWidth)
      const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - VIEWPORT_MARGIN - minWidth))
      next = { left, minWidth, maxWidth: window.innerWidth - VIEWPORT_MARGIN - left, ...vertical }
    } else {
      next = { left: rect.left, width: rect.width, ...vertical }
    }
    setPosition((prev) => (samePosition(prev, next) ? prev : next))
  }, [triggerRef, growToContent, maxListHeight])

  React.useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null)
      return
    }
    update()
    const onScroll = (event: Event) => {
      // The list scrolling under the cursor does not move the trigger.
      if (event.target instanceof Node && popoverRef.current?.contains(event.target)) return
      update()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', update)
    }
  }, [isOpen, update, popoverRef])

  return position
}
